import { Download } from '@carbon/icons-react';
import { Button } from '@carbon/react';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { getAbsoluteURL } from 'utils/utils';
import { formatFileSize } from 'utils/video';

import './VideoPlayer.scss';

/**
 * VideoPlayer component for displaying generated videos
 * Supports multiple videos with download functionality
 */
export const VideoPlayer = ({ videos, className = '' }) => {
  const { t } = useTranslation();
  const [loadErrors, setLoadErrors] = useState({});

  const handleVideoError = (videoId) => {
    setLoadErrors((prev) => ({
      ...prev,
      [videoId]: true,
    }));
  };

  const handleDownload = async (video) => {
    try {
      const videoUrl = getAbsoluteURL(video.url);
      const response = await fetch(videoUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = video.file_name || `video_${video.id}.mp4`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Failed to download video:', error);
      toast.error(t('Failed to download video. Please try again.'));
    }
  };

  if (!videos || videos.length === 0) {
    return null;
  }

  const getFileSizeDisplay = (video) => {
    if (video.file_size) {
      return formatFileSize(video.file_size / (1024 * 1024));
    }
    return null;
  };

  return (
    <div className={`video-player-container ${className}`}>
      {videos.length > 1 && (
        <div className='video-count-label'>
          {t('Generated {{count}} videos', { count: videos.length })}
        </div>
      )}

      <div className={`video-grid ${videos.length > 1 ? 'video-grid--multiple' : ''}`}>
        {videos.map((video) => (
          <div key={video.id} className='video-item'>
            {loadErrors[video.id] ? (
              <div className='video-error'>
                <p>{t('Failed to load video')}</p>
                <Button
                  kind='tertiary'
                  size='sm'
                  onClick={() => handleDownload(video)}
                  renderIcon={Download}
                >
                  {t('Download')}
                </Button>
              </div>
            ) : (
              <>
                <video
                  controls
                  className='video-element'
                  preload='metadata'
                  onError={() => handleVideoError(video.id)}
                  aria-label={t('Generated video {{index}}', { index: videos.indexOf(video) + 1 })}
                >
                  <source src={getAbsoluteURL(video.url)} type={video.content_type || 'video/mp4'} />
                  {t('Your browser does not support the video tag.')}
                </video>

                <div className='video-metadata'>
                  <div className='video-info'>
                    <span className='video-duration'>
                      {video.duration ? `${video.duration}s` : ''}
                    </span>
                    <span className='video-size'>{getFileSizeDisplay(video)}</span>
                  </div>

                  <Button
                    kind='ghost'
                    size='sm'
                    hasIconOnly
                    iconDescription={t('Download video')}
                    tooltipPosition='top'
                    onClick={() => handleDownload(video)}
                    renderIcon={Download}
                  />
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

VideoPlayer.propTypes = {
  videos: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      url: PropTypes.string.isRequired,
      file_name: PropTypes.string,
      content_type: PropTypes.string,
      duration: PropTypes.number,
      width: PropTypes.number,
      height: PropTypes.number,
      file_size: PropTypes.number,
      thumbnail_url: PropTypes.string,
    }),
  ).isRequired,
  className: PropTypes.string,
};

VideoPlayer.defaultProps = {
  className: '',
};

export default VideoPlayer;
